<template>
  <nav class="menu" data-qa="menu">
    <ul
      :class="['menu__list', { reversed: isShownAdditionalTab }]"
      ref="navWrapper"
      data-qa="desktop-header-navigation-list"
    >
      <li
        v-for="r in routes"
        :key="r.label"
        :class="['menu__item', { menu__item_aside: !r.route }]"
        :data-qa="r.label"
      >
        <router-link
          v-if="r.route && !r.external"
          @click="trackEvent(r.label)"
          :class="['menu__link', { 'router-link-active': isActiveTab(r) }]"
          :to="r.route"
          >{{ r.label }}</router-link
        >
        <a v-else-if="r.route && r.external" @click="trackEvent(r.label)" class="menu__link" :href="r.route as string">{{
          r.label
        }}</a>
        <div
          v-else
          :class="['menu__aside', 'aside-menu', { 'aside-menu__active': isActiveTab(r) }]"
        >
          <button
            class="aside-menu__label aside-menu__label_dropdown"
            :aria-label="r.label"
            :aria-controls="`submenu-${r.label}`"
            aria-expanded="false"
            @keyup.enter="keyboardOpenMenuHandler"
          >
            {{ r.label }}
          </button>

          <ul class="aside-menu__list" :id="`submenu-${r.label}`">
            <li v-for="(cr, count) in r.childRoutes" :key="cr.label" class="aside-menu__item">
              <router-link
                v-if="!cr.external"
                @click="trackEvent(cr.label)"
                class="aside-menu__link"
                :to="cr.route as RouteLocationRaw"
                @keyup.tab="
                  (e) => {
                    keyboardCloseMenuHandler(e, count, r.childRoutes?.length as number)
                  }
                "
                >{{ cr.label }}</router-link
              >
              <a
                v-else
                class="aside-menu__link"
                @click="trackEvent(cr.label)"
                @keyup.tab="
                  (e) => {
                    keyboardCloseMenuHandler(e, count, r.childRoutes?.length as number)
                  }
                "
                :href="cr.route as string"
                target="_blank"
                rel="noopener"
                >{{ cr.label }}</a
              >
            </li>
          </ul>
        </div>
      </li>
      <li v-if="isShownAdditionalTab" class="menu__item"></li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
  import { useGtm } from '@gtm-support/vue-gtm';
  import { computed } from 'vue';
  import { RouteLocationRaw, useRoute } from 'vue-router';

  import { NavigationLink } from '@/types/interfaces';

  const trackEvent = (linkText: string) => {
      const gtm = useGtm();
      gtm?.trackEvent({
        event: 'simple_interaction',
        elementType: 'link',
        elementName: 'Desktop Header Navigation',
        elementText: linkText,
      });
    };

  const props = defineProps<{
    routes: NavigationLink[]
  }>();

  const route = useRoute();

  const isShownAdditionalTab = computed(() => props.routes.length === 1);

  const isActiveTab = (routeTab: NavigationLink): boolean => {
    if (routeTab.additionalRoute) {
      return routeTab.additionalRoute.name === route.matched[0].name;
    }
    if (routeTab.childRoutes) {
      return !!routeTab.childRoutes.find((childRoute) => childRoute.name === route.name);
    }
    return false;
  };

  const keyboardOpenMenuHandler = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    const ulMenuEl = target.nextElementSibling as HTMLElement;

    if (target.getAttribute('aria-expanded') == 'true') {
      target.setAttribute('aria-expanded', 'false');
      ulMenuEl.classList.remove('active');
    } else {
      target.setAttribute('aria-expanded', 'true');
      ulMenuEl.classList.add('active');
    }
  };

  const keyboardCloseMenuHandler = (
    event: KeyboardEvent,
    count: number,
    countLength: number,
  ): void => {
    if (count == countLength - 1) {
      const target = event.target as HTMLElement;
      const ulMenuEl = target?.parentElement?.parentElement as HTMLElement;
      const useActionsMenu = document?.querySelector(
        '.header__user-actions_desktop .user-actions__toggle-dropdown',
      ) as HTMLElement;
      const menuButton = ulMenuEl?.previousElementSibling as HTMLElement;

      menuButton.setAttribute('aria-expanded', 'false');
      ulMenuEl.classList.remove('active');

      const nextMenuItemToFocus =
        ulMenuEl.parentElement?.parentElement?.nextElementSibling?.querySelector('a');

      if (nextMenuItemToFocus) nextMenuItemToFocus.focus();
      else {
        useActionsMenu.focus();
      }
    }
  };
</script>

<style scoped lang="scss">
  .menu {
    &__list {
      display: flex;
      justify-content: flex-start;
      width: 100%;
    }

    &__item {
      list-style-type: none;
      display: block;
      white-space: nowrap;

      &:first-child {
        margin-left: 0;
      }
    }

    &__link {
      display: block;
      justify-content: center;
      min-width: 100px;
      padding: 0 12px;
      font-size: remc(15px);
      font-weight: 500;
      line-height: remc($tabs-header-height);
      color: $white;
      text-decoration: none;
      background: #1c2335;
      border-radius: 2px 2px 0 0;

      &:hover {
        background: #5d6270;
        border-bottom: 4px solid #5d6270;
      }

      &.router-link-active {
        background: #1c2335;
        color: $brand-primary-100-teal;
        border-bottom: 4px solid $brand-primary-100-teal;
      }
    }

    &__aside {
      background: $white;
    }
  }

  .aside-menu {
    $p: &;

    position: relative;
    display: flex;
    justify-content: center;
    min-width: 100px;
    color: $link-text;
    text-decoration: none;
    background: #1c2335;
    border-radius: 2px 2px 0 0;

    &:hover {
      background: $light-navy-5;

      #{$p}__list {
        display: block;
      }
      background: #5d6270;
      border-bottom: 4px solid #5d6270;
    }

    &__label {
      padding: 0 12px;
      font-size: remc(15px);
      font-weight: 500;
      line-height: remc($tabs-header-height);
      color: $white;
      background: transparent;

      &_dropdown {
        padding: 0 30px 0 12px;

        &:after {
          position: absolute;
          rotate: 180deg;
          top: 50%;
          right: 15px;
          width: 9px;
          height: 9px;
          content: '';
          background-color: transparent;
          background-repeat: no-repeat;
          background-position: center;
          transform: translate(0, 50%);
          background-image: url('@/assets/icons/chevron-white-dropdown.svg');
        }
      }
    }

    &__active {
      background: #1c2335;
      color: $brand-primary-100-teal;
      border-bottom: 4px solid $brand-primary-100-teal;

      .aside-menu__label_dropdown {
        color: $brand-primary-100-teal;

        &:after {
          background-image: url('@/assets/icons/chevron-teal-dropdown.svg');
        }
      }

      &:hover {
        background: $main-bg;
      }
    }

    &__list {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 1;
      display: none;
      width: 200px;
      background: $white;
      border: 1px solid $light-navy-40;
      border-top: none;
      border-radius: 0 0 4px 4px;

      &:hover {
        display: block;
      }

      &.active {
        display: block;
      }
    }

    &__item {
      border-bottom: 1px solid $light-navy-40;

      &:last-child {
        border-bottom: none;
      }
    }

    &__link {
      display: block;
      padding: 8px 16px;
      font-size: remc(12px);
      line-height: remc(20px);
      color: $link-text;

      &:hover {
        color: $text-100-charcoal;
        font-weight: 700;
      }
    }
  }
</style>
